import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ClipLoader from 'react-spinners/ClipLoader';
import {
	Activity, AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight,
	Clock, Code2, Copy, FileText, Globe, ImageIcon, Layers, Server, Send, XCircle,
} from 'lucide-react';

import { get_survey_response } from '../../actions/surveyActions';
import { get_gepp_inspection, domainFromSource } from '../../actions/geppActions';
import { epochToLocalDate } from '../../utils/dateConverter';
import isEmpty from '../../utils/isEmpty';
import Loading from '../utils/Loading';
import { Icons as PnIcons } from '../payloadNew/Icons';
import '../payloadNew/payloadNew.css';

import './surveyInspection.css';

// Server-side status codes → tone we render. Kept small on purpose: the
// backend already gives us `monitor_status_text` ("Good"/"Warning"/etc.), so
// tone is used just to color the pill.
const MONITOR_TONE_BY_STATUS = { 4: 'ok', 3: 'warn', 2: 'attn', 1: 'attn' };

const REQUEST_METHOD_BY_INT = { 1: 'GET', 2: 'POST', 3: 'POST', 4: 'PUT', 5: 'DELETE' };

const fmtDate = (epochMs) => (epochMs ? epochToLocalDate(epochMs, localStorage.getItem('i18nextLng')) : '—');

const fmtDuration = (ms) => {
	if (ms === null || ms === undefined) return '—';
	if (ms < 1000) return `${ms} ms`;
	const s = ms / 1000;
	if (s < 60) return `${s.toFixed(2)} s`;
	const m = Math.floor(s / 60);
	const rem = (s - m * 60).toFixed(0);
	return `${m}m ${rem}s`;
};

const fmtBytes = (b) => {
	if (b === null || b === undefined) return '—';
	if (b < 1024) return `${b} B`;
	if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
	return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

// ─── JSON viewer ──────────────────────────────────────────────────────────

const escapeHtml = (s) => s
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;');

// Regex-based JSON highlighter — safe because input is escaped first, so the
// only markup this function inserts is our own <span> tags. Order matters:
// strings must be matched before booleans/numbers so their inner tokens
// aren't styled twice.
const highlightJson = (str) => str.replace(
	/("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
	(match, strLit, colon, kw, num) => {
		if (strLit) {
			if (colon) return `<span class="jv-key">${strLit}</span>${colon}`;
			return `<span class="jv-str">${strLit}</span>`;
		}
		if (kw) return `<span class="jv-kw">${kw}</span>`;
		if (num) return `<span class="jv-num">${num}</span>`;
		return match;
	}
);

const JsonViewer = ({ data, title }) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);
	const text = useMemo(() => (
		typeof data === 'string' ? data : JSON.stringify(data, null, 2)
	), [data]);
	const lines = useMemo(() => text.split('\n'), [text]);
	const resolvedTitle = title || t('surveys.response.inspection.jsonTitle', { defaultValue: 'json' });

	const onCopy = async () => {
		let ok = false;
		if (navigator.clipboard && window.isSecureContext) {
			try { await navigator.clipboard.writeText(text); ok = true; }
			catch (e) { ok = false; }
		}
		if (!ok) {
			const ta = document.createElement('textarea');
			ta.value = text;
			ta.setAttribute('readonly', '');
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			ta.style.left = '-9999px';
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
			document.body.removeChild(ta);
		}
		if (ok) {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	return (
		<div className='jv'>
			<header className='jv-head'>
				<span className='jv-title'>{resolvedTitle}</span>
				<button
					type='button'
					className='jv-copy'
					onClick={onCopy}
					aria-label={t('surveys.response.inspection.copyJson', { defaultValue: 'Copy JSON' })}
				>
					{copied ? <Check size={12} /> : <Copy size={12} />}
					<span>{copied
						? t('surveys.response.inspection.copied', { defaultValue: 'Copied' })
						: t('surveys.response.inspection.copy', { defaultValue: 'Copy' })}</span>
				</button>
			</header>
			<div className='jv-scroll term-scroll'>
				<pre className='jv-pre'>
					{lines.map((line, i) => (
						<div className='jv-row' key={i}>
							<span className='jv-lno'>{i + 1}</span>
							<code
								className='jv-code'
								dangerouslySetInnerHTML={{ __html: highlightJson(escapeHtml(line)) || '&nbsp;' }}
							/>
						</div>
					))}
				</pre>
			</div>
		</div>
	);
};

const CheckPill = ({ status, label }) => {
	const tone = status === 'ok' ? 'ok' : status === 'warn' ? 'warn' : 'attn';
	const Icon = tone === 'ok' ? CheckCircle2 : tone === 'warn' ? AlertTriangle : XCircle;
	return (
		<span className={`si-pill si-pill--${tone}`}>
			<Icon size={12} /> {label}
		</span>
	);
};

const Section = ({ title, icon: Icon, right, children, defaultOpen = true, collapsible = false }) => {
	const [open, setOpen] = useState(defaultOpen);
	const collapsed = collapsible && !open;
	return (
		<section className={`si-card${collapsed ? ' is-collapsed' : ''}`}>
			<header
				className={`si-card-head${collapsible ? ' is-collapsible' : ''}${collapsed ? ' is-collapsed' : ''}`}
				onClick={collapsible ? () => setOpen((v) => !v) : undefined}
			>
				<h2 className='si-card-title'>
					{collapsible && (open ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
					{Icon && <Icon size={15} />}
					<span>{title}</span>
				</h2>
				{right && <div className='si-card-right'>{right}</div>}
			</header>
			{open && <div className='si-card-body'>{children}</div>}
		</section>
	);
};

const KeyVal = ({ k, v, mono }) => (
	<div className='si-kv'>
		<div className='si-kv-key'>{k}</div>
		<div className={`si-kv-val${mono ? ' is-mono' : ''}`}>{v ?? '—'}</div>
	</div>
);

// ─── Sections ─────────────────────────────────────────────────────────────

const HeroStats = ({ summary, monitorText }) => {
	if (!summary) return null;
	const items = [
		{ k: 'Images', v: summary.total_images ?? 0 },
		{ k: 'Results', v: summary.total_results ?? 0 },
		{ k: 'Documents', v: summary.total_documents ?? 0 },
		{ k: 'Files missing', v: summary.files_missing ?? 0, tone: (summary.files_missing ?? 0) > 0 ? 'attn' : 'muted' },
		{ k: 'Checks failed', v: summary.checks_failed ?? 0, tone: (summary.checks_failed ?? 0) > 0 ? 'attn' : 'muted' },
		{ k: 'Failed logs', v: summary.failed_logs ?? 0, tone: (summary.failed_logs ?? 0) > 0 ? 'attn' : 'muted' },
		{ k: 'Failed requests', v: summary.failed_requests ?? 0, tone: (summary.failed_requests ?? 0) > 0 ? 'attn' : 'muted' },
		{ k: 'Monitor', v: monitorText ?? '—', tone: (summary.monitor_status === 4 ? 'ok' : (summary.monitor_status === 3 ? 'warn' : 'attn')) },
	];
	return (
		<div className='si-hero-stats'>
			{items.map((it) => (
				<div key={it.k} className={`si-hero-stat${it.tone ? ` si-hero-stat--${it.tone}` : ''}`}>
					<div className='si-hero-stat-label'>{it.k}</div>
					<div className='si-hero-stat-value'>{it.v}</div>
				</div>
			))}
		</div>
	);
};

const ChecksBlock = ({ checks }) => {
	if (!Array.isArray(checks) || checks.length === 0) return <p className='si-empty'>No checks reported.</p>;
	return (
		<ul className='si-checks'>
			{checks.map((c, i) => {
				const tone = c.status === 'ok' ? 'ok' : c.status === 'warn' ? 'warn' : 'attn';
				const Icon = tone === 'ok' ? CheckCircle2 : tone === 'warn' ? AlertTriangle : XCircle;
				return (
					<li key={i} className={`si-check si-check--${tone}`}>
						<span className='si-check-icon'><Icon size={16} /></span>
						<div>
							<div className='si-check-name'>{c.check}</div>
							<div className='si-check-msg'>{c.message}</div>
						</div>
					</li>
				);
			})}
		</ul>
	);
};

const GEPP_STEP_ORDER = ['RECIBIDO', 'ENCOLADO', 'PROCESADO', 'ENVIADO', 'COMPLETADO'];

// A log is treated as an error when the wrapper flag says so, when the HTTP
// status came back non-2xx, or when the entry carries an `error` payload.
// Any one of these paints the step red — the others stay green.
const isErrorLog = (log) => {
	if (!log) return false;
	if (log.success === false) return true;
	if (log.error != null) return true;
	if (typeof log.status === 'number' && (log.status < 200 || log.status >= 300)) return true;
	return false;
};

const GeppLogsStepper = ({ logs, reportedStatuses }) => {
	// Build a step→log map so we can show timing per gepp lifecycle step.
	const byStatus = {};
	(logs || []).forEach((l) => {
		const s = l?.payload?.estatus;
		if (s) byStatus[s] = l;
	});
	const seen = new Set(reportedStatuses || Object.keys(byStatus));
	return (
		<ol className='si-stepper'>
			{GEPP_STEP_ORDER.map((step, idx) => {
				const log = byStatus[step];
				const done = seen.has(step) || !!log;
				const errored = isErrorLog(log);
				const tone = done ? (errored ? 'attn' : 'ok') : 'idle';
				return (
					<li key={step} className={`si-step si-step--${tone}`}>
						<span className='si-step-marker'>{idx + 1}</span>
						<div className='si-step-body'>
							<div className='si-step-name'>{step}</div>
							{log ? (
								<div className='si-step-meta'>
									{fmtDate(log?.date?.$date)}
									{log?.duration != null && <span> · {log.duration} ms</span>}
									{log?.status != null && <span> · HTTP {log.status}</span>}
									{errored && log?.error && <span> · {typeof log.error === 'string' ? log.error : 'error'}</span>}
								</div>
							) : (
								<div className='si-step-meta si-muted'>pending</div>
							)}
						</div>
					</li>
				);
			})}
		</ol>
	);
};

const Timeline = ({ timeline }) => {
	const rows = useMemo(() => {
		const list = Array.isArray(timeline) ? timeline : [];
		return [...list].sort((a, b) => (a?.date?.$date ?? 0) - (b?.date?.$date ?? 0));
	}, [timeline]);
	const [expanded, setExpanded] = useState({});
	if (rows.length === 0) return <p className='si-empty'>No timeline events.</p>;
	const iconByType = { image_update: ImageIcon, step: Server, request: Send, log: Activity };
	return (
		<ol className='si-timeline'>
			{rows.map((ev, i) => {
				const Icon = iconByType[ev.type] || Clock;
				const isOpen = !!expanded[i];
				let detail = '';
				if (ev.type === 'log') {
					detail = `${ev.record?.payload?.estatus ?? ''} · trigger ${ev.record?.trigger ?? '?'}`;
				} else if (ev.type === 'request') {
					const m = REQUEST_METHOD_BY_INT[ev.record?.path?.method] ?? '';
					detail = `${m} ${ev.record?.path?.url ?? ''}`;
				} else if (ev.type === 'step') {
					detail = `${ev.record?.resource ?? ''} · status ${ev.record?.status ?? ''}`;
				} else if (ev.type === 'image_update') {
					detail = `${ev.record?.prev_status ?? '?'} → ${ev.record?.current_status ?? '?'}`;
				}
				return (
					<li key={i} className='si-tl-item'>
						<span className='si-tl-icon'><Icon size={14} /></span>
						<div className='si-tl-body'>
							<button
								type='button'
								className='si-tl-head'
								onClick={() => setExpanded((s) => ({ ...s, [i]: !s[i] }))}
							>
								{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
								<span className='si-tl-type'>{ev.type}</span>
								<span className='si-tl-detail'>{detail}</span>
								<span className='si-tl-date'>{fmtDate(ev?.date?.$date)}</span>
							</button>
							{isOpen && (
								<JsonViewer data={ev.record} title={`${ev.type} record`} />
							)}
						</div>
					</li>
				);
			})}
		</ol>
	);
};

const MonitorBlock = ({ monitors }) => {
	if (!Array.isArray(monitors) || monitors.length === 0) return <p className='si-empty'>No monitors.</p>;
	return (
		<div className='si-monitors'>
			{monitors.map((m, i) => {
				const mon = m.monitor || {};
				const tone = MONITOR_TONE_BY_STATUS[mon.status] || 'muted';
				const hasSteps = Array.isArray(m.steps) && m.steps.length > 0;
				return (
					<Fragment key={i}>
						{/* Monitor info as its own sub-card. */}
						<div className={`si-monitor si-monitor--${tone}`}>
							<div className='si-monitor-head'>
								<CheckPill status={tone === 'ok' ? 'ok' : tone === 'warn' ? 'warn' : 'attn'} label={`Status ${mon.status ?? '?'}`} />
								<span className='si-monitor-domain'>{mon.domain}</span>
							</div>
							<div className='si-grid si-grid--fixed7'>
								<KeyVal k='Duration' v={fmtDuration(mon.duration)} />
								<KeyVal k='Images' v={mon.num_images ?? 0} />
								<KeyVal k='Started' v={fmtDate(mon.date?.$date)} />
								<KeyVal k='Completed' v={fmtDate(mon.completed?.$date)} />
								<KeyVal k='Request' v={mon.request} mono />
								<KeyVal k='Payload' v={mon.payload?.$oid} mono />
							</div>
						</div>

						{/* Steps as label/value grid — same shape as the monitor
						    info card above, so both sub-cards read as a set. */}
						{hasSteps && (
							<div className='si-monitor'>
								<div className='si-subtitle'>Steps</div>
								{m.steps.map((s, j) => (
									<div
										key={j}
										className='si-grid si-grid--fixed7'
										style={{ marginTop: j === 0 ? 0 : 14, paddingTop: j === 0 ? 0 : 14, borderTop: j === 0 ? 'none' : '1px solid var(--ds-border, #e5e7eb)' }}
									>
										<KeyVal k='Resource' v={s.resource} />
										<KeyVal k='Status' v={s.status} />
										<KeyVal k='Queued' v={fmtDate(s.queued?.$date)} />
										<KeyVal k='Started' v={fmtDate(s.started?.$date)} />
										<KeyVal k='Completed' v={fmtDate(s.completed?.$date)} />
										<KeyVal k='Queue ms' v={s.queue} />
										<KeyVal k='Process ms' v={s.process} />
									</div>
								))}
							</div>
						)}
					</Fragment>
				);
			})}
		</div>
	);
};

const RequestsBlock = ({ requests }) => {
	const [expanded, setExpanded] = useState({});
	if (!Array.isArray(requests) || requests.length === 0) return <p className='si-empty'>No requests.</p>;
	return (
		<ul className='si-reqs'>
			{requests.map((r, i) => {
				const method = REQUEST_METHOD_BY_INT[r?.path?.method] ?? '?';
				const isOpen = !!expanded[i];
				const httpStatus = r?.result?.status;
				const httpTone = httpStatus >= 200 && httpStatus < 300 ? 'ok' : httpStatus >= 400 ? 'attn' : 'warn';
				return (
					<li key={i} className='si-req'>
						<button type='button' className='si-req-head' onClick={() => setExpanded((s) => ({ ...s, [i]: !s[i] }))}>
							{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
							<span className='si-req-method'>{method}</span>
							<span className='si-req-url'>{r?.path?.url}</span>
							<span className={`si-pill si-pill--${httpTone}`}>{httpStatus ?? '—'}</span>
							<span className='si-req-time'>{fmtDuration((r?.process ?? 0) * 1000)}</span>
						</button>
						{isOpen && (
							<div className='si-req-body'>
								<div className='si-grid'>
									<KeyVal k='Service' v={r?.service?.$oid} mono />
									<KeyVal k='Instance' v={r?.instance?.$oid} mono />
									<KeyVal k='Status' v={r?.status} />
									<KeyVal k='Failed' v={r?.failed} />
									<KeyVal k='Date' v={fmtDate(r?.date?.$date)} />
								</div>
								{r?.body && (
									<Fragment>
										<div className='si-subtitle'>Body</div>
										<JsonViewer data={r.body} title='request body' />
									</Fragment>
								)}
								{r?.result?.data && (
									<Fragment>
										<div className='si-subtitle'>Result data</div>
										<JsonViewer data={r.result.data} title='response data' />
									</Fragment>
								)}
							</div>
						)}
					</li>
				);
			})}
		</ul>
	);
};

const FilesBlock = ({ files }) => {
	if (!Array.isArray(files) || files.length === 0) return <p className='si-empty'>No files.</p>;
	return (
		<div className='si-table-wrap'>
			<table className='si-table'>
				<thead>
					<tr><th>Type</th><th>Reference</th><th>Filename</th><th>Size</th><th>Exists</th><th>Archived</th></tr>
				</thead>
				<tbody>
					{files.map((f, i) => (
						<tr key={i}>
							<td>{f.type}</td>
							<td className='is-mono'>{f.reference}</td>
							<td className='is-mono'>{f.filename}</td>
							<td>{fmtBytes(f.size)}</td>
							<td>
								{f.exists
									? <span className='si-pill si-pill--ok'><CheckCircle2 size={12} /> yes</span>
									: <span className='si-pill si-pill--attn'><XCircle size={12} /> no</span>
								}
							</td>
							<td>{f.archived ? 'yes' : 'no'}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const CountsBlock = ({ data }) => {
	const items = [
		{ k: 'Images', v: (data.images || []).length },
		{ k: 'Image updates', v: (data.image_updates || []).length },
		{ k: 'Image results', v: (data.image_results || []).length },
		{ k: 'Results', v: (data.results || []).length },
		{ k: 'Result relations', v: (data.result_relations || []).length },
		{ k: 'Documents', v: (data.documents || []).length },
		{ k: 'Document updates', v: (data.document_updates || []).length },
		{ k: 'Document results', v: (data.document_results || []).length },
		{ k: 'Rejections', v: (data.rejections || []).length },
	];
	return (
		<div className='si-grid'>
			{items.map((it) => (
				<KeyVal key={it.k} k={it.k} v={it.v} />
			))}
		</div>
	);
};

// ─── Page ─────────────────────────────────────────────────────────────────

const SurveyInspection = () => {
	const { t } = useTranslation();
	const { response_id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { survey_response, survey_loading } = useSelector((s) => s.survey);
	const { gepp_inspection, gepp_inspection_loading, gepp_errors } = useSelector((s) => s.gepp);

	// Reference/source come from the survey response (existing action).
	// The page is deep-linkable, so we always refetch on mount instead of
	// relying on Redux having the right response cached.
	useEffect(() => {
		if (response_id) dispatch(get_survey_response(response_id));
	}, [response_id]);

	const source = survey_response?.source;
	const reference = survey_response?.reference;
	const domain = useMemo(() => domainFromSource(source), [source]);

	useEffect(() => {
		if (domain && reference) {
			dispatch(get_gepp_inspection(domain, reference));
		}
	}, [domain, reference]);

	const inspection = gepp_inspection;
	const summary = inspection?.summary;
	const err = gepp_errors?.gepp_inspect?.msg;

	const loading = survey_loading || gepp_inspection_loading;

	if (loading && !inspection) {
		return <Loading msg={t('surveys.response.inspection.loading', { defaultValue: 'Loading inspection…' })} />;
	}

	return (
		<div id={localStorage.getItem('theme')}>
			<div className='payload-new'>
				<div className='si-shell si-page'>
					<button type='button' className='ds-back-link' onClick={() => navigate(`/surveys/responses/${response_id}`)}>
						<PnIcons.arrow style={{ transform: 'rotate(180deg)' }} />
						{t('surveys.response.inspection.back', { defaultValue: 'Back to survey' })}
					</button>

				{err && !inspection && (
					<div className='si-alert'>
						<AlertTriangle size={16} /> {err}
					</div>
				)}

				{!isEmpty(survey_response) && !domain && (
					<div className='si-alert si-alert--warn'>
						<AlertTriangle size={16} /> {t('surveys.response.inspection.noDomain', { defaultValue: 'This survey has no source; cannot inspect.' })}
					</div>
				)}

				{inspection && (
					<Fragment>
						{/* Hero card: eyebrow, title + status pill, meta chips
						    and the summary stats all in a single container so
						    it reads as one identity block. Matches the pattern
						    used by the payload/survey info cards. */}
						<section className='si-hero'>
							<div className='si-hero-head'>
								<div className='si-hero-main'>
									<div className='si-eyebrow'>
										<Activity size={12} /> {t('surveys.response.inspection.eyebrow', { defaultValue: 'GEPP inspection' })}
									</div>
									<h1 className='si-hero-title'>
										<span>
											{inspection?.record?.code ? `Code ${inspection.record.code}` : t('surveys.response.inspection.title', { defaultValue: 'Inspection' })}
										</span>
										{summary?.monitor_status_text && (
											<span className={`si-status-pill si-status-pill--${MONITOR_TONE_BY_STATUS[summary.monitor_status] || 'muted'}`}>
												{summary.monitor_status_text}
											</span>
										)}
									</h1>
									<div className='si-hero-meta'>
										{domain && <span className='si-chip'>domain: <strong>{domain}</strong></span>}
										{reference && <span className='si-chip is-mono'>ref: {reference}</span>}
									</div>
								</div>
							</div>
							{summary && (
								<Fragment>
									<div className='si-hero-divider' />
									<HeroStats summary={summary} monitorText={summary?.monitor_status_text} />
								</Fragment>
							)}
						</section>

						<Section title={t('surveys.response.inspection.checks', { defaultValue: 'Checks' })} icon={CheckCircle2}>
							<ChecksBlock checks={inspection.checks} />
						</Section>

						<Section title={t('surveys.response.inspection.timeline', { defaultValue: 'Timeline' })} icon={Clock}>
							<Timeline timeline={inspection.timeline} />
						</Section>

						<Section title={t('surveys.response.inspection.geppLogs', { defaultValue: 'GEPP status lifecycle' })} icon={Send}>
							<GeppLogsStepper logs={inspection.logs} reportedStatuses={summary?.reported_statuses} />
						</Section>

						<Section title={t('surveys.response.inspection.monitor', { defaultValue: 'Monitor & steps' })} icon={Server}>
							<MonitorBlock monitors={inspection.monitors} />
						</Section>

						<Section title={t('surveys.response.inspection.requests', { defaultValue: 'HTTP requests' })} icon={Globe}>
							<RequestsBlock requests={inspection.requests} />
						</Section>

						<Section title={t('surveys.response.inspection.files', { defaultValue: 'Files' })} icon={FileText}>
							<FilesBlock files={inspection.files} />
						</Section>

						<Section title={t('surveys.response.inspection.counts', { defaultValue: 'Related counts' })} icon={Layers} collapsible defaultOpen={false}>
							<CountsBlock data={inspection} />
						</Section>

						<Section title={t('surveys.response.inspection.raw', { defaultValue: 'Raw JSON' })} icon={Code2} collapsible defaultOpen={false}>
							<JsonViewer data={inspection} title='inspection payload' />
						</Section>
					</Fragment>
				)}

				{gepp_inspection_loading && inspection && (
					<div className='si-inline-loading'>
						<ClipLoader color='#004982' size={16} /> {t('surveys.response.inspection.refetching', { defaultValue: 'Refreshing…' })}
					</div>
				)}
				</div>
			</div>
		</div>
	);
};

export default SurveyInspection;
